/**
 * Shared reducer for the Invited App Shortcuts GRS recommendations response.
 *
 * Both Confluence surfaces — the nav4 `PlatformNavigation` renderer
 * (`InvitedAppShortcutsRecommendations.tsx`) and the legacy Apollo-interop one
 * (`GlobalNavigation/LegacyInvitedAppShortcutsRecommendations.tsx`) — read the
 * same operation and need the same two derived values, so the mapping lives
 * here rather than being duplicated per surface.
 */
import { useMemo } from 'react';

import { toRecommendedProducts } from '@atlassian/cross-join-nudges/common/utils/invited-app-shortcuts-utils';
import type {
	InvitedAppShortcutAllowedTargetAppKey,
	InvitedAppShortcutRecommendedApp,
} from '@atlassian/cross-join-nudges/types';
import { buildInvitedAppShortcutsRecommendationsResult } from '@atlassian/cross-join-nudges/ui/invited-app-shortcuts/invited-app-shortcuts-recommendations-provider';

import type { InvitedAppShortcutsRecommendationsQuery$data } from '../__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

/**
 * Stable empty reference so a miss does not produce a new array each render,
 * which would invalidate `targetProducts` and every downstream memo.
 */
const NO_RECOMMENDED_APPS: ReadonlyArray<InvitedAppShortcutRecommendedApp> = [];

export type UseRecommendedAppsResult = {
	/** Recommended target apps with the lozenge variant parsed from `entityId`. */
	recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedApp>;
	/**
	 * The same apps reduced to plain product keys, for the deliberately
	 * variant-agnostic `targetProducts` hook input.
	 */
	targetProducts: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
};

/**
 * Translate a raw AGG recommendations response into the values
 * `useInvitedAppShortcuts` needs.
 *
 * Keys off the parsed `product`, NOT the raw `entityId`: since PUMA-2245 GRS
 * encodes the lozenge variant into the id (`jira-animated-shortcut`), so an
 * entityId match would reject every recommendation. `product` is `null` for ids
 * that do not name a supported target app, which is how those get dropped.
 *
 * An `error` or `empty` result — and a not-yet-loaded `data`, which the interop
 * surface can hand us — all fall through to `[]`, which the hook treats as
 * "no recommended apps".
 *
 * @param data - The query response, or `undefined` while it is still loading
 * @returns The parsed recommended apps and their plain product keys
 */
export const useRecommendedApps = (
	data: InvitedAppShortcutsRecommendationsQuery$data | undefined,
): UseRecommendedAppsResult => {
	// GRS owns target eligibility and rollout gating. Both Confluence navigation surfaces consume
	// every supported target it returns so Nav4 and the legacy navigation cannot diverge.
	const recommendedApps = useMemo(() => {
		if (!data) {
			return NO_RECOMMENDED_APPS;
		}
		const result = buildInvitedAppShortcutsRecommendationsResult(data);
		if (result.kind !== 'recommendations') {
			return NO_RECOMMENDED_APPS;
		}
		return result.recommendations.reduce<InvitedAppShortcutRecommendedApp[]>((acc, rec) => {
			if (rec.product !== null) {
				acc.push({ product: rec.product, variant: rec.variant });
			}
			return acc;
		}, []);
	}, [data]);

	const targetProducts = useMemo(() => toRecommendedProducts(recommendedApps), [recommendedApps]);

	return { recommendedApps, targetProducts };
};
